import React, { useCallback, useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@material-ui/core";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import RefreshIcon from "@material-ui/icons/Refresh";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import { useTokenOps } from "../../../components/TokenContext";
import { hostname } from "../../../providers/api";
import {
  TraceabilityIssues,
  traceabilityResultFromHistory,
} from "../../traceabilityIssues";
import { pollUploadHistory } from "../traceabilityVersionUpdate";
import "./LastTraceabilityUploadSection.css";

// GET /projects/:id/lastTraceabilityUpload answers the latest traceability upload of the
// project: a manual UPLOAD, or the VERSION_UPDATE_UPLOAD of an auto-upload Update Version
// together with its VERSION_UPDATE row (changes applied). Version updates that only
// download the file are not uploads and are not returned.
const FINAL_STATUSES = ["SUCCESS", "PARTIAL", "FAILED"];
const MAX_CHANGES_SHOWN = 200;
const HIDDEN_STORAGE_KEY = "bcome-admin-last-traceability-upload-hidden";

const TYPE_LABELS = {
  UPLOAD: "Traceability upload",
  VERSION_UPDATE: "Version update · changes applied",
  VERSION_UPDATE_UPLOAD: "Version update · upload",
};

const STATUS_LABELS = {
  UPLOADED: "Uploaded",
  PROCESSING: "Processing",
  SUCCESS: "Success",
  PARTIAL: "Partial",
  FAILED: "Failed",
};

// The VERSION_UPDATE row stores { sourceVersion, targetVersion, changes } in `warnings`.
export const parseVersionUpdateChanges = (row) => {
  try {
    const payload = JSON.parse(row?.warnings);
    if (!payload || typeof payload !== "object") return null;
    return {
      sourceVersion: payload.sourceVersion,
      targetVersion: payload.targetVersion,
      changes: Array.isArray(payload.changes) ? payload.changes : [],
    };
  } catch {
    return null;
  }
};

const fetchLastProjectUpload = async (projectId, accessToken) => {
  const response = await fetch(`${hostname}/projects/${projectId}/lastTraceabilityUpload`, {
    headers: { authorization: "Bearer " + accessToken },
  });
  if (response.status >= 400) {
    throw new Error(`Last upload request failed (HTTP ${response.status})`);
  }
  const data = await response.json();
  return { upload: data?.upload || null, versionUpdate: data?.versionUpdate || null };
};

const fetchHistoryRow = async (id, accessToken) => {
  const response = await fetch(`${hostname}/uploadHistory/${id}`, {
    headers: { authorization: "Bearer " + accessToken },
  });
  if (response.status >= 400) {
    throw new Error(`Upload status request failed (HTTP ${response.status})`);
  }
  return response.json();
};

const readHidden = () => {
  try {
    return window.localStorage.getItem(HIDDEN_STORAGE_KEY) === "true";
  } catch {
    return false;
  }
};

const writeHidden = (hidden) => {
  try {
    window.localStorage.setItem(HIDDEN_STORAGE_KEY, String(hidden));
  } catch {
    /* preference is not kept, the section still works */
  }
};

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "-");

const FailureText = ({ row }) =>
  row.status === "FAILED" && row.stacktrace ? (
    <pre className="last-traceability-upload__stacktrace">{row.stacktrace}</pre>
  ) : null;

const UploadResult = ({ row }) => {
  const result = traceabilityResultFromHistory(row);
  if (result) {
    return (
      <TraceabilityIssues
        errors={result.errors}
        warnings={result.warnings}
        uploadedName={row.filename}
      />
    );
  }
  // Failures without row/column details (e.g. a version update that could not be
  // generated) are stored as plain text.
  return <FailureText row={row} />;
};

// Collapsed by default so the upload result below (e.g. a failure) is seen first.
const VersionUpdateChanges = ({ row }) => {
  const [expanded, setExpanded] = useState(false);
  const result = parseVersionUpdateChanges(row);
  if (!result) return <FailureText row={row} />;
  const { sourceVersion, targetVersion, changes } = result;
  const shown = changes.slice(0, MAX_CHANGES_SHOWN);

  return (
    <div className="last-traceability-upload__changes">
      <p className="last-traceability-upload__message">
        {sourceVersion || "?"} → {targetVersion || "?"} ·{" "}
        {changes.length === 1 ? "1 change" : `${changes.length} changes`}
        {shown.length > 0 && (
          <Button
            size="small"
            startIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
            style={{ marginLeft: 8 }}
          >
            {expanded ? "Hide changes" : "Show changes"}
          </Button>
        )}
      </p>
      {expanded && shown.length > 0 && (
        <div style={{ maxHeight: 400, overflow: "auto", marginTop: 8 }}>
          <Table size="small" aria-label="Version update changes" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Sheet</TableCell>
                <TableCell align="right">Row</TableCell>
                <TableCell>Column</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Old value</TableCell>
                <TableCell>New value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shown.map((change, index) => (
                <TableRow key={`${change.sheetName}-${change.row}-${change.column}-${index}`}>
                  <TableCell>{change.sheetName}</TableCell>
                  <TableCell align="right">{change.row}</TableCell>
                  <TableCell>{change.column}</TableCell>
                  <TableCell>{change.type}</TableCell>
                  <TableCell style={{ wordBreak: "break-word" }}>{change.oldValue}</TableCell>
                  <TableCell style={{ wordBreak: "break-word" }}>{change.newValue}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {expanded && changes.length > shown.length && (
        <p className="last-traceability-upload__meta">
          Showing {shown.length} of {changes.length} changes. The full list is in File uploads.
        </p>
      )}
    </div>
  );
};

const HistoryRow = ({ row, children }) => {
  const { status } = row;
  return (
    <div className="last-traceability-upload__row">
      <div className="last-traceability-upload__summary">
        <span
          className={`last-traceability-upload__status last-traceability-upload__status--${String(
            status || ""
          ).toLowerCase()}`}
        >
          {STATUS_LABELS[status] || status}
          {!FINAL_STATUSES.includes(status) && (
            <CircularProgress size={10} style={{ marginLeft: 6 }} color="inherit" />
          )}
        </span>
        <span>{TYPE_LABELS[row.type] || row.type}</span>
        <span className="last-traceability-upload__filename">{row.filename}</span>
      </div>
      <div className="last-traceability-upload__meta">
        Uploaded {formatDate(row.uploadDate)}
        {row.actorName ? ` by ${row.actorName}` : ""}
      </div>
      {children}
    </div>
  );
};

/**
 * Latest traceability upload of the project (manual or from an auto-upload version
 * update) with its status and errors/warnings; for an auto-upload, also the version
 * update row with the changes applied. Can be hidden; the choice is remembered in this
 * browser. `reloadKey` changes when this page starts or finishes an upload.
 */
const LastTraceabilityUploadSection = ({ record, reloadKey }) => {
  const tokenOps = useTokenOps();
  const accessToken = tokenOps.account?.accessToken;
  const [hidden, setHidden] = useState(readHidden);
  const [rows, setRows] = useState({ upload: null, versionUpdate: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadCount, setReloadCount] = useState(0);
  const projectId = record?.id;

  useEffect(() => {
    if (hidden || !projectId) return undefined;
    let cancelled = false;

    // Still running: follow the row until it finishes.
    const follow = async (key, row) => {
      if (!row || FINAL_STATUSES.includes(row.status)) return;
      const finalRow = await pollUploadHistory({
        fetchRow: () => fetchHistoryRow(row.id, accessToken),
        isCancelled: () => cancelled,
      });
      if (!cancelled && finalRow) setRows((prev) => ({ ...prev, [key]: finalRow }));
    };

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const latest = await fetchLastProjectUpload(projectId, accessToken);
        if (cancelled) return;
        setRows(latest);
        setLoading(false);
        await Promise.all([
          follow("versionUpdate", latest.versionUpdate),
          follow("upload", latest.upload),
        ]);
      } catch (err) {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [hidden, projectId, accessToken, reloadKey, reloadCount]);

  const toggleHidden = () => {
    setHidden((prev) => {
      writeHidden(!prev);
      return !prev;
    });
  };

  const reload = useCallback(() => setReloadCount((count) => count + 1), []);

  const { upload, versionUpdate } = rows;

  return (
    <div className="last-traceability-upload">
      <div className="last-traceability-upload__header">
        <h3 className="last-traceability-upload__title">Last traceability upload</h3>
        {!hidden && (
          <Tooltip title="Reload">
            <span>
              <IconButton size="small" onClick={reload} disabled={loading} aria-label="Reload">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
        <Button
          size="small"
          startIcon={hidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
          onClick={toggleHidden}
          aria-expanded={!hidden}
        >
          {hidden ? "Show" : "Hide"}
        </Button>
      </div>

      {!hidden && (
        <div className="last-traceability-upload__body">
          {loading && !upload ? (
            <CircularProgress size={20} />
          ) : error ? (
            <p className="last-traceability-upload__message last-traceability-upload__message--error">
              Could not load the last upload: {error}
            </p>
          ) : !upload ? (
            <p className="last-traceability-upload__message">
              No traceability upload or auto-upload version update in the upload history (kept for 3 months).
            </p>
          ) : (
            <>
              {versionUpdate && (
                <HistoryRow row={versionUpdate}>
                  <VersionUpdateChanges row={versionUpdate} />
                </HistoryRow>
              )}
              <HistoryRow row={upload}>
                <UploadResult row={upload} />
              </HistoryRow>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default LastTraceabilityUploadSection;
